import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Select, SelectItem, Tag } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../../api/client';
import { BlockSkeleton, ErrorState, InlineError } from '../../components/states';
import { HE_A_ERRORS } from '../../components/labels';
import type { Command } from '../Commands';
import { MoneyInput } from '../../components/MoneyInput';

// Ban poker admin tao (Texas, Omaha). Cau hinh he A poker_ban_tao: tao / sua / xoa tung ban; nguoi choi thay
// ban moi trong danh sach ban ngay. Sua: muc cuoc va mua vao (loai game, so ghe co dinh tu luc tao). Xoa ban
// dang co nguoi: van dang choi chay het, chip tra ve vi tung nguoi dung mot lan.

type Gid = 'vgcg_6' | 'vgcg_12';
type BanTao = { id: number; gid: Gid; muc: number; muaToiThieu: number; muaToiDa: number; soGhe: number };
type BanLive = { id: string; soNguoi: number; soGhe: number; trangThai: string; banTao: number | null };
type Game = { gid: string; ten: string; ban?: BanLive[]; soGheBanTao?: number[] };
type Data = { games: Game[]; banTao: { ban: BanTao[]; soCuoi: number }; ban_loi?: string };
type Form = { gid: Gid; soGhe: string; muc: string; muaToiThieu: string; muaToiDa: string };
type KetQua = { command: Command; ban: BanTao };

const TEN_GAME: Record<Gid, string> = { vgcg_6: "Texas Hold'em", vgcg_12: "Omaha Hold'em" };
const TRANG_THAI: Record<string, string> = { DANG_CHOI: 'Đang chơi', CHO_VAN: 'Chờ ván', DANG_DONG: 'Đang đóng', LOI_TAT_TOAN: 'Lỗi trả chip' };
const digits = (s: string) => s.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 15);

// Loi cua form (cung luat voi API va he A), null neu gui duoc
function loiForm(f: Form, soGhe?: number[]): string | null {
  const muc = Number(f.muc);
  const min = Number(f.muaToiThieu);
  const max = Number(f.muaToiDa);
  if (!muc || !min || !max) return 'Nhập đủ mức cược và tiền mua vào';
  if (min < 2 * muc) return 'Mua vào tối thiểu phải từ một mù lớn (2 x mức cược) trở lên';
  if (max < min) return 'Mua vào tối đa phải lớn hơn hoặc bằng tối thiểu';
  if (soGhe && !soGhe.includes(Number(f.soGhe))) return 'Chọn số ghế';
  return null;
}

export function PokerBanTaoTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['poker-tables'],
    queryFn: () => api.get<Data>('/api/admin/games/poker/tables'),
    refetchInterval: 3000,
  });
  const [form, setForm] = useState<Form>({ gid: 'vgcg_6', soGhe: '', muc: '', muaToiThieu: '', muaToiDa: '' });
  const [sua, setSua] = useState<(Form & { id: number }) | null>(null);
  const [xoa, setXoa] = useState<BanTao | null>(null);
  const [kq, setKq] = useState<{ viec: string; r: KetQua } | null>(null);
  const xong = (viec: string) => (r: KetQua) => { setKq({ viec, r }); qc.invalidateQueries({ queryKey: ['poker-tables'] }); };

  const tao = useMutation({
    mutationFn: (f: Form) => api.post<KetQua>('/api/admin/games/poker/ban-tao', {
      gid: f.gid, soGhe: Number(f.soGhe), muc: Number(f.muc), muaToiThieu: Number(f.muaToiThieu), muaToiDa: Number(f.muaToiDa),
    }),
    onSuccess: (r) => { xong('Tạo')(r); setForm((f) => ({ ...f, muc: '', muaToiThieu: '', muaToiDa: '' })); },
  });
  const luuSua = useMutation({
    mutationFn: (f: Form & { id: number }) => api.put<KetQua>('/api/admin/games/poker/ban-tao/' + f.id, {
      muc: Number(f.muc), muaToiThieu: Number(f.muaToiThieu), muaToiDa: Number(f.muaToiDa),
    }),
    onSuccess: (r) => { xong('Sửa')(r); setSua(null); },
  });
  const luuXoa = useMutation({
    mutationFn: (id: number) => api.del<KetQua>('/api/admin/games/poker/ban-tao/' + id),
    onSuccess: (r) => { xong('Xóa')(r); setXoa(null); },
  });

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const game = d.games.find((g) => g.gid === form.gid);
  const soGhe = game?.soGheBanTao ?? [];
  const loiTao = loiForm(form, soGhe);
  const live = new Map<string, BanLive>();
  for (const g of d.games) for (const b of g.ban ?? []) if (b.banTao != null) live.set(g.gid + ':' + b.banTao, b);
  const conTrongCauHinh = new Set(d.banTao.ban.map((b) => b.gid + ':' + b.id));
  // ban da xoa nhung con dang dong (con nguoi / dang het van)
  const dangDong = d.games.flatMap((g) => (g.ban ?? []).filter((b) => b.banTao != null && !conTrongCauHinh.has(g.gid + ':' + b.banTao)).map((b) => ({ ...b, gid: g.gid })));
  const nhap = (k: keyof Form, tienTe = true) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: tienTe ? digits(e.target.value) : e.target.value }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}>
      {d.ban_loi && <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Máy game chưa trả lời: chưa tạo được bàn mới." />}
      {kq && <KetQuaLenh viec={kq.viec} r={kq.r} onClose={() => setKq(null)} />}

      <form
        aria-label="Tạo bàn poker"
        style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}
        onSubmit={(e) => { e.preventDefault(); if (!loiTao) tao.mutate(form); }}
      >
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Tạo bàn mới</h3>
        <p className="muted" style={{ margin: '0 0 1rem' }}>Bàn tiền thật Texas hoặc Omaha. Người chơi thấy bàn trong danh sách bàn ngay sau khi tạo.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', alignItems: 'start' }}>
          <Select id="bt-gid" labelText="Loại game" value={form.gid} onChange={(e) => setForm((f) => ({ ...f, gid: e.target.value as Gid, soGhe: '' }))}>
            <SelectItem value="vgcg_6" text={TEN_GAME.vgcg_6} />
            <SelectItem value="vgcg_12" text={TEN_GAME.vgcg_12} />
          </Select>
          <Select id="bt-ghe" labelText="Số ghế" value={form.soGhe} onChange={nhap('soGhe', false)}>
            <SelectItem value="" text="Chọn" />
            {soGhe.map((n) => <SelectItem key={n} value={String(n)} text={n + ' ghế'} />)}
          </Select>
          <MoneyInput id="bt-muc" labelText="Mức cược (mù nhỏ)" helperText={form.muc ? 'Mù lớn ' + formatVnd(2 * Number(form.muc)) : ' '}
            className="money-input" value={form.muc} onChange={(v) => setForm((f) => ({ ...f, muc: v }))} />
          <MoneyInput id="bt-min" labelText="Mua vào tối thiểu" helperText=" " className="money-input" value={form.muaToiThieu} onChange={(v) => setForm((f) => ({ ...f, muaToiThieu: v }))} />
          <MoneyInput id="bt-max" labelText="Mua vào tối đa" helperText=" " className="money-input" value={form.muaToiDa} onChange={(v) => setForm((f) => ({ ...f, muaToiDa: v }))} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
          <Button type="submit" size="md" disabled={!!loiTao || tao.isPending || !!d.ban_loi}>{tao.isPending ? 'Đang tạo...' : 'Tạo bàn'}</Button>
          {loiTao && (form.muc || form.muaToiThieu || form.muaToiDa) ? <span className="muted">{loiTao}</span> : null}
        </div>
        {tao.error ? <InlineError error={tao.error} /> : null}
      </form>

      <div style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>Bàn admin đã tạo</h3>
        {d.banTao.ban.length === 0 && dangDong.length === 0 ? <p className="muted">Chưa có bàn nào.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Bàn admin đã tạo" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--cds-border-subtle)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Bàn</th>
                  <th style={{ padding: '0.5rem' }}>Game</th>
                  <th style={{ padding: '0.5rem' }}>Mức cược</th>
                  <th style={{ padding: '0.5rem' }}>Mua vào</th>
                  <th style={{ padding: '0.5rem' }}>Người ngồi</th>
                  <th style={{ padding: '0.5rem' }}>Trạng thái</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {d.banTao.ban.map((b) => {
                  const l = live.get(b.gid + ':' + b.id);
                  return (
                    <tr key={b.gid + b.id} style={{ borderBottom: '1px solid var(--cds-border-subtle)' }}>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>#{b.id}{l ? ' · ' + l.id : ''}</td>
                      <td style={{ padding: '0.5rem' }}>{TEN_GAME[b.gid]}</td>
                      <td style={{ padding: '0.5rem' }}>{formatVnd(b.muc)} / {formatVnd(2 * b.muc)}</td>
                      <td style={{ padding: '0.5rem' }}>{formatVnd(b.muaToiThieu)} - {formatVnd(b.muaToiDa)}</td>
                      <td style={{ padding: '0.5rem' }}>{l ? l.soNguoi : 0} / {b.soGhe}</td>
                      <td style={{ padding: '0.5rem' }}>
                        {l ? <Tag type={l.trangThai === 'DANG_CHOI' ? 'green' : l.trangThai === 'CHO_VAN' ? 'blue' : 'red'}>{TRANG_THAI[l.trangThai] ?? l.trangThai}</Tag>
                          : <Tag type="gray">Chưa thấy trên máy game</Tag>}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" kind="ghost" onClick={() => setSua({ id: b.id, gid: b.gid, soGhe: String(b.soGhe), muc: String(b.muc), muaToiThieu: String(b.muaToiThieu), muaToiDa: String(b.muaToiDa) })}>Sửa</Button>
                        <Button size="sm" kind="danger--ghost" onClick={() => setXoa(b)}>Xóa</Button>
                      </td>
                    </tr>
                  );
                })}
                {dangDong.map((b) => (
                  <tr key={'dong' + b.id} style={{ borderBottom: '1px solid var(--cds-border-subtle)' }}>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>#{b.banTao} · {b.id}</td>
                    <td style={{ padding: '0.5rem' }}>{TEN_GAME[b.gid as Gid] ?? b.gid}</td>
                    <td colSpan={2} className="muted" style={{ padding: '0.5rem' }}>Đã xóa, đang trả chip cho người còn ngồi</td>
                    <td style={{ padding: '0.5rem' }}>{b.soNguoi} / {b.soGhe}</td>
                    <td style={{ padding: '0.5rem' }}><Tag type="red">{TRANG_THAI[b.trangThai] ?? b.trangThai}</Tag></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sua && (
        <Modal open modalHeading={`Sửa bàn #${sua.id} (${TEN_GAME[sua.gid]}, ${sua.soGhe} ghế)`} primaryButtonText={luuSua.isPending ? 'Đang lưu...' : 'Lưu'}
          secondaryButtonText="Hủy" primaryButtonDisabled={!!loiForm(sua) || luuSua.isPending}
          onRequestClose={() => setSua(null)} onRequestSubmit={() => luuSua.mutate(sua)}>
          <p className="muted" style={{ marginBottom: '1rem' }}>Áp dụng cho người vào bàn sau và từ ván sau. Loại game và số ghế giữ như lúc tạo.</p>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <MoneyInput id="bs-muc" labelText="Mức cược (mù nhỏ)" className="money-input" value={sua.muc} onChange={(v) => setSua({ ...sua, muc: v })} />
            <MoneyInput id="bs-min" labelText="Mua vào tối thiểu" className="money-input" value={sua.muaToiThieu} onChange={(v) => setSua({ ...sua, muaToiThieu: v })} />
            <MoneyInput id="bs-max" labelText="Mua vào tối đa" className="money-input" value={sua.muaToiDa} onChange={(v) => setSua({ ...sua, muaToiDa: v })} />
            {loiForm(sua) ? <span className="muted">{loiForm(sua)}</span> : null}
            {luuSua.error ? <InlineError error={luuSua.error} /> : null}
          </div>
        </Modal>
      )}

      {xoa && (
        <Modal open danger modalHeading={`Xóa bàn #${xoa.id} (${TEN_GAME[xoa.gid]})?`} primaryButtonText={luuXoa.isPending ? 'Đang xóa...' : 'Xóa bàn'}
          secondaryButtonText="Hủy" primaryButtonDisabled={luuXoa.isPending}
          onRequestClose={() => setXoa(null)} onRequestSubmit={() => luuXoa.mutate(xoa.id)}>
          <p>Bàn ngừng nhận người mới ngay. Ván đang chơi chạy hết, rồi chip của từng người đang ngồi được trả về ví (một lần), sau đó bàn biến khỏi danh sách.</p>
          {luuXoa.error ? <InlineError error={luuXoa.error} /> : null}
        </Modal>
      )}
    </div>
  );
}

function KetQuaLenh({ viec, r, onClose }: { viec: string; r: KetQua; onClose: () => void }) {
  const ok = r.command.status === 'SUCCEEDED';
  const code = r.command.lastError || (r.command.result && typeof r.command.result.loi === 'string' ? r.command.result.loi : '');
  return (
    <div>
      <InlineNotification kind={ok ? 'success' : 'warning'} lowContrast onClose={() => { onClose(); return false; }}
        title={ok ? `${viec} bàn #${r.ban.id}: xong` : `${viec} bàn #${r.ban.id}: lệnh chưa xong`}
        subtitle={ok ? 'Máy game đã nhận.' : (HE_A_ERRORS[code] ?? (code || r.command.status))} />
      <p><Link className="cell-link" to={'/commands/' + r.command.id}>Xem lệnh</Link></p>
    </div>
  );
}
